import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useIntl } from "react-intl";
import { advertisementsApiService } from "../../../cores/advertisements/services/api";
import type { Adset } from "../../../cores/advertisements/interfaces/api/get-adsets";
import type { Campaign } from "../../../cores/advertisements/interfaces/api/get-campaigns";
import { CampaignStatus } from "../../../cores/advertisements/constants/campaign-status";
import { useAdvertisementCore } from "../../../cores/advertisements";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useMeCore } from "../../../cores/me";
import moment from "moment";
import ActivateButton from "./ActivateButton";
import { formatAdMoney, normalizeAdCurrencyCode } from "./adMoneyFormat";
import "./CampaignsTable.scss";

function columnCellStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    flex: "0 0 auto",
  };
}

export type FlatAdsetRow = Adset & {
  campaignId: string;
  campaignName: string;
};

type Props = {
  campaigns: Campaign[];
};

const COLS = [
  { key: "status", width: 120 },
  { key: "name", width: 260 },
  { key: "platform", width: 100 },
  { key: "campaign", width: 200 },
  { key: "spend", width: 140 },
  { key: "impressions", width: 130 },
  { key: "clicks", width: 110 },
] as const;

export default function AdsetsFlatTable({ campaigns }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { settings } = useFacebookAdsCore();
  const currencyCode = normalizeAdCurrencyCode(settings?.currency);
  const { putAdset } = useAdvertisementCore();
  const [rows, setRows] = useState<FlatAdsetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (!company?.id || !campaigns.length) {
      setRows([]);
      setLoadError(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    const merged: FlatAdsetRow[] = [];
    try {
      for (const c of campaigns) {
        const { adsets } = await advertisementsApiService.getAdsets(
          company.id,
          c.id
        );
        for (const a of adsets) {
          merged.push({
            ...a,
            campaignId: c.id,
            campaignName: c.name,
          });
        }
      }
      setRows(merged);
      setLoadError(false);
    } catch (e: unknown) {
      console.error(e);
      setRows([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [campaigns, company?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.campaignName || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const handleToggleAdset = useCallback(
    (row: FlatAdsetRow) => {
      const end = campaigns.find((c) => c.id === row.campaignId)?.stopTime;
      if (!end || moment().isBefore(end)) {
        if (row.status === CampaignStatus.ACTIVE.id) {
          putAdset(
            company?.id!,
            row.id,
            CampaignStatus.PAUSED.id,
            String(row.dailyBudget)
          );
        } else if (row.status === CampaignStatus.PAUSED.id) {
          putAdset(
            company?.id!,
            row.id,
            CampaignStatus.ACTIVE.id,
            String(row.dailyBudget)
          );
        }
      }
    },
    [campaigns, company?.id, putAdset]
  );

  return (
    <div className="overflow-auto h-100 fb-campaigns-table">
      <div className="fb-campaigns-table__toolbar">
        <div className="fb-campaigns-table__toolbar-start">
          <div className="fb-campaigns-table__search-wrap">
            <FontAwesomeIcon
              className="fb-campaigns-table__search-icon"
              icon={["fas", "search"]}
            />
            <input
              type="search"
              className="form-control fb-campaigns-table__search"
              placeholder={intl.formatMessage({
                id: "app.advertisements.tableSearchPlaceholder",
              })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <button
          type="button"
          className="fb-campaigns-table__icon-btn"
          onClick={() => load()}
          title={intl.formatMessage({
            id: "app.advertisements.toolbar.refresh",
          })}
        >
          <FontAwesomeIcon icon={["fas", "sync-alt"]} />
        </button>
        <span
          className="fb-campaigns-table__currency-pill"
          title={intl.formatMessage(
            { id: "app.advertisements.currencyHint" },
            { currency: currencyCode }
          )}
        >
          {currencyCode}
        </span>
        <span className="fb-campaigns-table__meta">
          {intl.formatMessage(
            { id: "app.advertisements.adsetsFlatCount" },
            { count: filtered.length }
          )}
        </span>
      </div>

      <div className="fb-campaigns-table__head-row">
        {COLS.map((c) => (
          <div
            key={c.key}
            className="header fb-campaigns-table__col-head"
            style={columnCellStyle(c.width)}
          >
            {intl.formatMessage({
              id: `app.advertisements.adsetsFlatCol.${c.key}`,
            })}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="fb-campaigns-table__empty">
          {intl.formatMessage({ id: "app.advertisements.flatLoading" })}
        </div>
      ) : loadError ? (
        <div className="fb-campaigns-table__flat-error">
          <p className="fb-campaigns-table__flat-error-text">
            {intl.formatMessage({
              id: "app.advertisements.flatLoadErrorAdsets",
              defaultMessage:
                "We couldn't load ad sets. If Meta is rate-limiting, wait a few minutes and tap Retry.",
            })}
          </p>
          <button
            type="button"
            className="fb-campaigns-table__flat-retry"
            onClick={() => load()}
          >
            {intl.formatMessage({
              id: "app.advertisements.flatRetry",
              defaultMessage: "Retry",
            })}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="fb-campaigns-table__empty">
          {intl.formatMessage({ id: "app.advertisements.adsetsFlatEmpty" })}
        </div>
      ) : (
        filtered.map((row) => (
          <div
            className="table fb-campaigns-table__campaign"
            key={`${row.campaignId}-${row.id}`}
          >
            <div className="table-row fb-campaigns-table__row">
              {COLS.map((col) => (
                <div
                  key={col.key}
                  className={cn(
                    "table-cell fb-campaigns-table__cell",
                    col.key === "name" &&
                      "justify-content-start fb-campaigns-table__name-cell"
                  )}
                  style={columnCellStyle(col.width)}
                >
                  {col.key === "status" && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <ActivateButton
                        status={row.status}
                        endDate={
                          campaigns.find((c) => c.id === row.campaignId)
                            ?.stopTime ?? ""
                        }
                        onClick={() => handleToggleAdset(row)}
                      />
                    </span>
                  )}
                  {col.key === "name" && (
                    <span className="fb-campaigns-table__name-text">
                      {row.name}
                    </span>
                  )}
                  {col.key === "platform" && (
                    <span className="fb-campaigns-table__meta-platform">
                      <FontAwesomeIcon icon={["fab", "meta"]} />
                    </span>
                  )}
                  {col.key === "campaign" && (
                    <span>{row.campaignName}</span>
                  )}
                  {col.key === "spend" &&
                    formatAdMoney(row.spend, currencyCode)}
                  {col.key === "impressions" && row.impressions}
                  {col.key === "clicks" && row.clicks}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
